export interface CenterAddress {
  street: string;
  zip: string;
  city: string;
  label?: string;
  openedAt?: string;
  isNewUntil?: string;
  openingHours?: string;
  phone?: string;
  email?: string;
  publicTransport?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface CityProvider {
  name: string;
  donorNumber: string;
  bonusInfo: string;
  maxBonus: string;
  addresses: CenterAddress[];
}

export interface CityData {
  name: string;
  slug: string;
  providers: CityProvider[];
  coordinates?: {
    lat: number;
    lng: number;
  };
}

/**
 * Checks if a center was recently opened (e.g. within the last 90 days / ~3 months)
 * or before a specified isNewUntil cutoff date.
 */
export const isCenterNew = (
  openedAt?: string,
  isNewUntil?: string
): boolean => {
  if (!openedAt && !isNewUntil) return false;
  const now = new Date();

  if (isNewUntil) {
    const validUntil = new Date(isNewUntil);
    const startDate = openedAt ? new Date(openedAt) : new Date(0);
    return now >= startDate && now <= validUntil;
  }

  if (openedAt) {
    const openDate = new Date(openedAt);
    const diffMs = now.getTime() - openDate.getTime();
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    return diffMs >= 0 && diffMs <= ninetyDaysMs;
  }

  return false;
};

export const isAddressNew = (addr: CenterAddress): boolean => {
  return isCenterNew(addr.openedAt, addr.isNewUntil);
};

export const isCityNew = (city: CityData): boolean => {
  return city.providers.some((p) =>
    p.addresses.some((addr) => isAddressNew(addr))
  );
};

const PSE_BASE = {
  name: "Plasma Service Europe",
  donorNumber: "321030",
  bonusInfo:
    "Gestaffelt über die ersten 10 Spenden: Je 25 € nach der 2., 4., 6. und 10. Spende.",
  maxBonus: "100 €",
};

const CSL_BASE = {
  name: "CSL Plasma",
  donorNumber: "CSL24HZVJ",
  bonusInfo:
    "Gestaffelt über die ersten 10 Spenden: 25 € nach der 3., 50 € nach der 6. und 75 € nach der 10. Spende.",
  maxBonus: "150 €",
};

const pse = (addresses: CenterAddress[]): CityProvider => ({
  ...PSE_BASE,
  addresses,
});
const csl = (addresses: CenterAddress[]): CityProvider => ({
  ...CSL_BASE,
  addresses,
});

export const cities: CityData[] = [
  // PSE only
  {
    name: "Aachen",
    slug: "aachen",
    providers: [
      pse([
        {
          street: "Alter Posthof 1",
          zip: "52062",
          city: "Aachen",
          label: "Kapuziner Karree",
          coordinates: { lat: 50.7714, lng: 6.0863 },
        },
      ]),
    ],
    coordinates: { lat: 50.7753, lng: 6.0839 },
  },
  {
    name: "Darmstadt",
    slug: "darmstadt",
    providers: [
      pse([
        {
          street: "Ernst-Ludwig-Straße 12",
          zip: "64283",
          city: "Darmstadt",
          label: "Town House",
          coordinates: { lat: 49.8718, lng: 8.6543 },
        },
      ]),
    ],
    coordinates: { lat: 49.8728, lng: 8.6512 },
  },
  {
    name: "Dortmund",
    slug: "dortmund",
    providers: [
      pse([
        {
          street: "Kampstraße 47",
          zip: "44137",
          city: "Dortmund",
          label: "Innenstadt",
          coordinates: { lat: 51.515, lng: 7.4583 },
        },
      ]),
    ],
    coordinates: { lat: 51.5136, lng: 7.4653 },
  },
  {
    name: "Halle (Saale)",
    slug: "halle-saale",
    providers: [
      pse([
        {
          street: "Marktplatz 7",
          zip: "06108",
          city: "Halle (Saale)",
          coordinates: { lat: 51.4828, lng: 11.9697 },
        },
      ]),
    ],
    coordinates: { lat: 51.4828, lng: 11.9646 },
  },
  {
    name: "Hannover",
    slug: "hannover",
    providers: [
      pse([
        {
          street: "Schillerstraße 23",
          zip: "30159",
          city: "Hannover",
          label: "Hauptbahnhof",
          coordinates: { lat: 52.3757, lng: 9.7378 },
        },
      ]),
    ],
    coordinates: { lat: 52.3759, lng: 9.732 },
  },
  {
    name: "Magdeburg",
    slug: "magdeburg",
    providers: [
      pse([
        {
          street: "Alter Markt 12",
          zip: "39104",
          city: "Magdeburg",
          label: "Alter Markt",
          coordinates: { lat: 52.1318, lng: 11.6382 },
        },
      ]),
    ],
    coordinates: { lat: 52.1308, lng: 11.6289 },
  },
  {
    name: "Merseburg",
    slug: "merseburg",
    providers: [
      pse([
        {
          street: "Gotthardstraße 35",
          zip: "06217",
          city: "Merseburg",
          label: "Kliapassage",
          coordinates: { lat: 51.3553, lng: 11.9977 },
        },
      ]),
    ],
    coordinates: { lat: 51.3547, lng: 11.9992 },
  },
  {
    name: "Nordhausen",
    slug: "nordhausen",
    providers: [
      pse([
        {
          street: "Rautenstraße 8",
          zip: "99734",
          city: "Nordhausen",
          label: "Rathausplatz",
          coordinates: { lat: 51.5003, lng: 10.7943 },
        },
      ]),
    ],
    coordinates: { lat: 51.5006, lng: 10.7915 },
  },
  {
    name: "Rostock",
    slug: "rostock",
    providers: [
      pse([
        {
          street: "Kröpeliner Straße 25",
          zip: "18055",
          city: "Rostock",
          label: "Kröpeliner Tor Center",
          coordinates: { lat: 54.0883, lng: 12.136 },
        },
      ]),
    ],
    coordinates: { lat: 54.0924, lng: 12.1287 },
  },
  {
    name: "Wuppertal",
    slug: "wuppertal",
    providers: [
      pse([
        {
          street: "Friedrichstraße 33",
          zip: "42105",
          city: "Wuppertal",
          label: "Karlsplatz",
          coordinates: { lat: 51.2607, lng: 7.1465 },
        },
      ]),
    ],
    coordinates: { lat: 51.2562, lng: 7.1508 },
  },
  // CSL only
  {
    name: "Berlin",
    slug: "berlin",
    providers: [
      csl([
        {
          street: "Alexanderstraße 3",
          zip: "10178",
          city: "Berlin",
          label: "Alexanderplatz",
          coordinates: { lat: 52.5236, lng: 13.4151 },
        },
        {
          street: "Prerower Platz 3",
          zip: "13051",
          city: "Berlin",
          label: "Linden-Center",
          coordinates: { lat: 52.5639, lng: 13.505 },
        },
      ]),
    ],
    coordinates: { lat: 52.52, lng: 13.405 },
  },
  {
    name: "Bielefeld",
    slug: "bielefeld",
    providers: [
      csl([
        {
          street: "Bahnhofstraße 28",
          zip: "33602",
          city: "Bielefeld",
          label: "Jahnplatz",
          coordinates: { lat: 52.0257, lng: 8.5326 },
        },
      ]),
    ],
    coordinates: { lat: 52.0302, lng: 8.5325 },
  },
  {
    name: "Bonn",
    slug: "bonn",
    providers: [
      csl([
        {
          street: "Rabinstraße 1",
          zip: "53111",
          city: "Bonn",
          label: "Busbahnhof",
          coordinates: { lat: 50.7333, lng: 7.0937 },
        },
      ]),
    ],
    coordinates: { lat: 50.7374, lng: 7.0982 },
  },
  {
    name: "Braunschweig",
    slug: "braunschweig",
    providers: [
      csl([
        {
          street: "Damm 16",
          zip: "38100",
          city: "Braunschweig",
          label: "Schloss",
          coordinates: { lat: 52.2625, lng: 10.5243 },
        },
      ]),
    ],
    coordinates: { lat: 52.2689, lng: 10.5268 },
  },
  {
    name: "Bremen",
    slug: "bremen",
    providers: [
      csl([
        {
          street: "An der Weide 27-29",
          zip: "28195",
          city: "Bremen",
          label: "City",
          coordinates: { lat: 53.0807, lng: 8.8168 },
        },
        {
          street: "Hans-Bredow-Straße 19",
          zip: "28307",
          city: "Bremen",
          label: "Weserpark",
          coordinates: { lat: 53.0487, lng: 8.9554 },
        },
      ]),
    ],
    coordinates: { lat: 53.0793, lng: 8.8017 },
  },
  {
    name: "Duisburg",
    slug: "duisburg",
    providers: [
      csl([
        {
          street: "Schwanenstraße 21",
          zip: "47051",
          city: "Duisburg",
          label: "Innenstadt",
          coordinates: { lat: 51.435, lng: 6.76 },
        },
      ]),
    ],
    coordinates: { lat: 51.4344, lng: 6.7623 },
  },
  {
    name: "Frankfurt",
    slug: "frankfurt",
    providers: [
      csl([
        {
          street: "Stiftstraße 9-17",
          zip: "60313",
          city: "Frankfurt am Main",
          label: "Zeil",
          coordinates: { lat: 50.1153, lng: 8.6824 },
        },
      ]),
    ],
    coordinates: { lat: 50.1109, lng: 8.6821 },
  },
  {
    name: "Gelsenkirchen",
    slug: "gelsenkirchen",
    providers: [
      csl([
        {
          street: "Bahnhofstraße 12-14",
          zip: "45879",
          city: "Gelsenkirchen",
          label: "Altstadt",
          coordinates: { lat: 51.5093, lng: 7.0962 },
        },
      ]),
    ],
    coordinates: { lat: 51.5175, lng: 7.0857 },
  },
  {
    name: "Göttingen",
    slug: "goettingen",
    providers: [
      csl([
        {
          street: "Weender Straße 75",
          zip: "37073",
          city: "Göttingen",
          label: "Carré",
          coordinates: { lat: 51.5364, lng: 9.9341 },
        },
      ]),
    ],
    coordinates: { lat: 51.5413, lng: 9.9158 },
  },
  {
    name: "Hamburg",
    slug: "hamburg",
    providers: [
      csl([
        {
          street: "Hamburger Straße 45",
          zip: "22083",
          city: "Hamburg",
          label: "Hamburger Meile",
          openedAt: "2026-10-06",
          isNewUntil: "2027-01-06",
          openingHours: "Mo–Fr: 12:00 – 19:00 Uhr",
          phone: "+49 40 809035 580",
          email: "hamburg@cslplasma.com",
          publicTransport: "U3 Mundsburg / Hamburger Straße",
          coordinates: { lat: 53.5739, lng: 10.0335 },
        },
      ]),
    ],
    coordinates: { lat: 53.5511, lng: 9.9937 },
  },
  {
    name: "Kiel",
    slug: "kiel",
    providers: [
      csl([
        {
          street: "Sophienblatt 98-100",
          zip: "24114",
          city: "Kiel",
          label: "Hauptbahnhof",
          coordinates: { lat: 54.3097, lng: 10.1259 },
        },
      ]),
    ],
    coordinates: { lat: 54.3233, lng: 10.1227 },
  },
  {
    name: "Leipzig",
    slug: "leipzig",
    providers: [
      csl([
        {
          street: "Brühl 1",
          zip: "04109",
          city: "Leipzig",
          label: "Höfe am Brühl",
          openedAt: "2026-05-21",
          openingHours: "Mo–Fr: 12:00 – 19:00 Uhr, Sa: 08:00 – 14:00 Uhr",
          phone: "+49 341 33976400",
          email: "leipzig@cslplasma.com",
          publicTransport: "Leipzig Hbf (3 Min. Fußweg)",
          coordinates: { lat: 51.3418, lng: 12.3734 },
        },
      ]),
    ],
    coordinates: { lat: 51.3397, lng: 12.3731 },
  },
  {
    name: "Mainz",
    slug: "mainz",
    providers: [
      csl([
        {
          street: "Emmeransstr. 27 / Adolf-Kolping-Str. 4",
          zip: "55116",
          city: "Mainz",
          label: "Römerpassage",
          coordinates: { lat: 50.0014, lng: 8.2689 },
        },
      ]),
    ],
    coordinates: { lat: 49.9929, lng: 8.2473 },
  },
  {
    name: "Marburg",
    slug: "marburg",
    providers: [
      csl([
        {
          street: "Universitätsstraße 14-22",
          zip: "35037",
          city: "Marburg",
          label: "Kaufhaus Ahrens",
          coordinates: { lat: 50.8065, lng: 8.7673 },
        },
      ]),
    ],
    coordinates: { lat: 50.8022, lng: 8.7668 },
  },
  {
    name: "Nürnberg",
    slug: "nuernberg",
    providers: [
      csl([
        {
          street: "Gleißbühlstraße 16",
          zip: "90402",
          city: "Nürnberg",
          label: "Hauptbahnhof",
          coordinates: { lat: 49.4485, lng: 11.0836 },
        },
      ]),
    ],
    coordinates: { lat: 49.4521, lng: 11.0768 },
  },
  {
    name: "Wiesbaden",
    slug: "wiesbaden",
    providers: [
      csl([
        {
          street: "Kirchgasse 6",
          zip: "65185",
          city: "Wiesbaden",
          label: "LuisenForum",
          coordinates: { lat: 50.0802, lng: 8.2386 },
        },
      ]),
    ],
    coordinates: { lat: 50.0826, lng: 8.2415 },
  },
  // Both providers
  {
    name: "Karlsruhe",
    slug: "karlsruhe",
    providers: [
      pse([
        {
          street: "Kaiserstraße 146-148",
          zip: "76133",
          city: "Karlsruhe",
          label: "Europaplatz",
          coordinates: { lat: 49.0102, lng: 8.3947 },
        },
      ]),
      csl([
        {
          street: "Victor-Gollancz-Straße 5",
          zip: "76137",
          city: "Karlsruhe",
          label: "Hauptbahnhof",
          coordinates: { lat: 48.993, lng: 8.3971 },
        },
      ]),
    ],
    coordinates: { lat: 49.0069, lng: 8.4037 },
  },
  {
    name: "Köln",
    slug: "koeln",
    providers: [
      pse([
        {
          street: "Genovevastr. 5",
          zip: "51065",
          city: "Köln",
          label: "Mülheim",
          coordinates: { lat: 50.9625, lng: 7.0057 },
        },
        {
          street: "Hohenstaufenring 62",
          zip: "50674",
          city: "Köln",
          label: "Neustadt",
          coordinates: { lat: 50.9334, lng: 6.9401 },
        },
      ]),
      csl([
        {
          street: "Breite Straße 80-90",
          zip: "50667",
          city: "Köln",
          label: "Quincy",
          coordinates: { lat: 50.939, lng: 6.9487 },
        },
      ]),
    ],
    coordinates: { lat: 50.9375, lng: 6.9603 },
  },
  {
    name: "Münster",
    slug: "muenster",
    providers: [
      pse([
        {
          street: "Ludgeristraße 100",
          zip: "48143",
          city: "Münster",
          label: "Münster Arkaden",
          coordinates: { lat: 51.9604, lng: 7.6263 },
        },
      ]),
      csl([
        {
          street: "Salzstraße 26-28",
          zip: "48143",
          city: "Münster",
          label: "Im Salzhof",
          coordinates: { lat: 51.9608, lng: 7.6328 },
        },
      ]),
    ],
    coordinates: { lat: 51.9607, lng: 7.6261 },
  },
].sort((a, b) => a.name.localeCompare(b.name, "de"));

export const getCityBySlug = (slug: string): CityData | undefined => {
  return cities.find((city) => city.slug === slug);
};
